import * as React from 'react';
import { Form } from 'antd';
import { NodeBaseModel } from '../models';
import { InstanceUtils, FormUtils } from '../misc';
import { NodeFormValues } from '../types';
import { useNodeSubscriptions, useNodeFormValues } from '.';
import { useAppStore } from '../stores';
import { InputUtils } from '../misc';
import { InputBaseModel } from '../models';
import type { ContractCause } from '../history/ContractDraftHistory';

export default (newNode?: NodeBaseModel, previousNode?: NodeBaseModel) => {
    const { inputsStore } = useAppStore();

    const [formKey, setFormKey] = React.useState(0);

    const [form] = Form.useForm<NodeFormValues>();

    const isInitialLoad = React.useRef(true);

    const setNewNodeFormFieldValue = React.useCallback(
        (comparisonInput: InputBaseModel) => {
            FormUtils.applyInputValue(form, comparisonInput);

            const contractInput = inputsStore.inputs.find(i => i.guid === comparisonInput.guid);

            // If contract input is not found, it means that the input is new and has unsaved changes, otherwise compare the values
            comparisonInput.setHasUnsavedChanges(
                contractInput
                    ? !InputUtils.areValuesEqual(contractInput.dataType, contractInput.value, comparisonInput.value)
                    : true
            );
        },
        [form, inputsStore]
    );

    const setPreviousNodeFormFieldValue = React.useCallback(
        (input: InputBaseModel) => FormUtils.applyInputValue(form, input),
        [form]
    );

    useNodeFormValues(newNode, setNewNodeFormFieldValue);
    useNodeFormValues(previousNode, setPreviousNodeFormFieldValue);
    useNodeSubscriptions(form, newNode, setNewNodeFormFieldValue);

    const handleInputValuesChange = (node: NodeBaseModel, inputGuid: string, value: unknown, cause?: ContractCause) => {
        const input = node.getInputByGuid(inputGuid);

        if (!input) {
            return;
        }

        input.setValue(value);

        input.childInputs
            .filter(InstanceUtils.isInputWithSource)
            .forEach(childInput => childInput.fetchSourceOptionsHierarchy(cause));
    };

    const onValuesChange = (changedValues: NodeFormValues) => {
        if (!newNode) {
            return;
        }

        const guids = Object.keys(changedValues);
        const inputs = guids
            .map(guid => newNode.getInputByGuid(guid))
            .filter((input): input is InputBaseModel => !!input);
        const values = inputs.map(input => form.getFieldValue(input.guid));
        newNode.history.editInputs(inputs, (input, index, cause) =>
            handleInputValuesChange(newNode, input.guid, values[index], cause)
        );

        if (newNode.hasError && newNode.isValid) {
            newNode.setHasError(false);
        }
    };

    React.useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        setFormKey(prevKey => prevKey + 1);
    }, [newNode]);

    return {
        form,
        formKey,
        onValuesChange
    };
};
